"use client";

import React, { useEffect, useMemo, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay } from "swiper/modules";
import "swiper/css";
import Image from "next/image";
import Link from "next/link";
import { toast } from "sonner";
import { 
  ShieldCheck, 
  Truck, 
  RotateCcw, 
  CheckCircle, 
  Lock, 
  Heart, 
  ChevronRight, 
  FileText, 
  HelpCircle,
  ChevronDown,
  ShoppingCart
} from "lucide-react";

import { Product, ProductDetail, Variant, ProductImage } from "@/packages/types/products";
import { useCurrency } from "@/packages/context/CurrencyContext";
import { useCart } from "@/packages/context/CartContext";
import { useWishlist } from "@/packages/context/wishlistContext";
import { useAuth } from "@/packages/context/AuthContext";
import { useFastRouter } from "@/packages/hooks/useFastRouter";
import { getAssetPath } from "@/utils/assetPath";

import Price from "@/components/common/Price";
import RatingStars from "@/components/common/RatingStar";
import { useFreeShippingThreshold } from "@/packages/hooks/useFreeShippingThreshold";
import QuantitySelector from "@/components/common/QuantitySelector";
import SavePercentage from "@/components/common/SavedPercentage";
import Loader from "@/components/common/LoaderComponent";
import NextImage from "@/components/common/NextImage";
import PeopleAlsoViewed from "@/components/productdetail/PeopleAlsoViewed";
import InterestedProducts from "@/components/productdetail/InterestedProduct";

import { HEADER_V2_DESIGN_SYSTEM } from "@/components/header/v2/designTokens";
import LABELS from "@/components/global-constant/src/general";

type Props = {
  product: ProductDetail;
  relatedProducts: Product[];
  alsoBought: Product[];
  schemaCountry?: string;
};

export default function ProductDetailClient({
  product: initialProduct,
  relatedProducts: initialRelated,
  alsoBought: initialAlsoBought,
  schemaCountry = LABELS.DEFAULT_COUNTRY || "United States",
}: Props) {
  const { exchangeRate, code, symbol } = useCurrency();
  const freeShippingThreshold = useFreeShippingThreshold();
  const [quantity, setQuantity] = useState(1);
  const [product] = useState<ProductDetail | null>(initialProduct);
  const [alsoBought] = useState<Product[]>(initialAlsoBought ?? []);
  const [relatedProducts] = useState<Product[]>(initialRelated ?? []);
  
  const { fastPush } = useFastRouter();
  const { cartItems, addToCart, buyNow } = useCart();
  const { toggleWishlist, isInWishlist } = useWishlist();
  const { isLoggedIn } = useAuth();

  // Variant States
  const [selectedVariant, setSelectedVariant] = useState<Variant | undefined>(undefined);
  const [selectedAttributes, setSelectedAttributes] = useState<Record<string, string>>({});
  const [isInCart, setIsInCart] = useState(false);

  // Gallery States
// eslint-disable-next-line react-hooks/rules-of-hooks
  const imageUrls = useMemo(
    () => product?.images.map((img: ProductImage) => img.imageUrl) || [],
    [product]
  );
  const [selectedImage, setSelectedImage] = useState<string>(() => {
    if (initialProduct && initialProduct.images && initialProduct.images.length > 0) {
      if (initialProduct.variants && initialProduct.variants.length > 0) {
        const defaultVar = initialProduct.variants[0];
        const matchedImage = initialProduct.images.find((img) => img.id === defaultVar.imageId);
        if (matchedImage) return matchedImage.imageUrl;
      }
      return initialProduct.images[0].imageUrl;
    }
    return "";
  });
  const [manualImageSelected, setManualImageSelected] = useState(false);
  const [isZoomModalOpen, setIsZoomModalOpen] = useState(false);
  const [mainImgError, setMainImgError] = useState(false);

  useEffect(() => {
    setMainImgError(false);
  }, [selectedImage]);

  // Hover Zoom State (Desktop)
  const [zoomStyle, setZoomStyle] = useState({ transform: "scale(1)", transformOrigin: "center" });

  // Tab State
  const [activeTab, setActiveTab] = useState<"description" | "reviews" | "faqs">("description");
  const [openFaqIndexes, setOpenFaqIndexes] = useState<number[]>([0]);

  const toggleFaq = (idx: number) => {
    setOpenFaqIndexes((prev) =>
      prev.includes(idx) ? prev.filter((i) => i !== idx) : [...prev, idx]
    );
  };
  // Sync selected variant & attributes on mount or when product changes
  useEffect(() => {
    if (!product || !product.variants || product.variants.length === 0) {
      setSelectedVariant(undefined);
      setSelectedAttributes({});
      return;
    }

    const defaultVariant = product.variants[0];
    const defaultAttrs: Record<string, string> = {};
    defaultVariant.variantAttributes.forEach((attr) => {
      defaultAttrs[attr.attribute.name] = attr.attribute_value.name;
    });

    setSelectedVariant(defaultVariant);
    setSelectedAttributes(defaultAttrs);
    setQuantity(1);
  }, [product]);

  // Sync selected image with variant
  useEffect(() => {
    if (manualImageSelected || !product) return;

    if (selectedVariant) {
      const matchedImage = product.images.find((img) => img.id === selectedVariant.imageId);
      setSelectedImage(matchedImage?.imageUrl || imageUrls[0] || "");
    } else {
      setSelectedImage(imageUrls[0] || "");
    }
  }, [selectedVariant, product, imageUrls, manualImageSelected]);

  // Sync cart status
  useEffect(() => {
    if (!product) {
      setIsInCart(false);
      return;
    }

    const found = cartItems.some((item) => {
      const sameProduct = item.id === product.id;
      if (!selectedVariant) return sameProduct;
      const sameVariant = (item.variant?.id ?? null) === (selectedVariant?.id ?? null);
      return sameProduct && sameVariant;
    });

    setIsInCart(found);
  }, [cartItems, product, selectedVariant]);



  if (!product) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Loader label="Loading products..." size="lg" />
      </div>
    );
  }

  // Variant Helpers
  const attributeNames = Array.from(
    new Set(
      product.variants.flatMap((v) =>
        v.variantAttributes.map((attr) => attr.attribute.name)
      )
    )
  );

  const getAttributeValues = (attrName: string) => {
    return Array.from(
      new Set(
        product.variants
          .flatMap((v) => v.variantAttributes)
          .filter((attr) => attr.attribute.name === attrName)
          .map((attr) => attr.attribute_value.name)
      )
    );
  };

  const isValueValid = (attrName: string, value: string) => {
    const baseAttr = attributeNames[0];
    if (attrName === baseAttr) return true;

    return product.variants.some((variant) => {
      const matchesBase = variant.variantAttributes.some(
        (attr) =>
          attr.attribute.name === baseAttr &&
          attr.attribute_value.name === selectedAttributes[baseAttr]
      );
      const matchesCurrent = variant.variantAttributes.some(
        (attr) =>
          attr.attribute.name === attrName &&
          attr.attribute_value.name === value
      );
      return matchesBase && matchesCurrent;
    });
  };

  const handleAttributeSelect = (attrName: string, value: string) => {
    const updated = { ...selectedAttributes, [attrName]: value };

    const matchedVariant = product.variants.find((variant) =>
      variant.variantAttributes.every(
        (attr) => updated[attr.attribute.name] === attr.attribute_value.name
      )
    );

    if (matchedVariant) {
      const matchedImage = product.images.find((img) => img.id === matchedVariant.imageId);
      setSelectedImage(matchedImage?.imageUrl || imageUrls[0]);
      setManualImageSelected(false);
      setSelectedVariant(matchedVariant);
      setSelectedAttributes(updated);
      setQuantity(1);
    } else {
      const fallback = product.variants.find((variant) =>
        variant.variantAttributes.some(
          (attr) =>
            attr.attribute.name === attrName &&
            attr.attribute_value.name === value
        )
      );
      if (fallback) {
        const fallbackAttrs: Record<string, string> = {};
        fallback.variantAttributes.forEach((attr) => {
          fallbackAttrs[attr.attribute.name] = attr.attribute_value.name;
        });

        const matchedImage = product.images.find((img) => img.id === fallback.imageId);
        setSelectedImage(matchedImage?.imageUrl || imageUrls[0]);
        setManualImageSelected(false);
        setSelectedVariant(fallback);
        setSelectedAttributes(fallbackAttrs);
        setQuantity(1);
      }
    }
  };

  // Pricing calculations
  const displaySalePrice = selectedVariant ? Number(selectedVariant.salePrice) : product.salePrice;
  const displayOriginalPrice = selectedVariant ? Number(selectedVariant.comparePrice) : (product.comparePrice ?? 0);
  const hasDiscount = displayOriginalPrice > displaySalePrice;
  // const discountValue = hasDiscount ? displayOriginalPrice - displaySalePrice : 0;
  const stockCount = selectedVariant ? selectedVariant.stock : product.stock;

  // Actions
  const handleQuantityChange = (newQty: number) => {
    if (!selectedVariant) {
      setQuantity(newQty);
      return;
    }
    const existing = cartItems.find(
      (item) => item.id === product.id && (item.variant?.id ?? null) === (selectedVariant.id ?? null)
    );
    const inCartQty = existing ? existing.quantity : 0;

    if (newQty <= selectedVariant.stock - inCartQty) {
      setQuantity(newQty);
    } else {
      toast.error(`Only ${selectedVariant.stock - inCartQty} items left in stock`);
    }
  };

  const handleAddToCart = () => {
    if (product.variants && product.variants.length > 0) {
      if (!selectedVariant) return;
      addToCart(product, quantity, selectedVariant);
    } else {
      addToCart(product, quantity);
    }
  };

  const handleBuyNow = () => {
    if (product.variants && product.variants.length > 0) {
      if (!selectedVariant) return;
      buyNow(product, quantity, selectedVariant);
      fastPush("/checkout?from=buyNow");
    } else {
      buyNow(product, quantity);
      fastPush("/checkout?from=buyNow");
    }
  };

  // Hover zoom triggers
  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const { left, top, width, height } = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - left) / width) * 100;
    const y = ((e.clientY - top) / height) * 100;
    setZoomStyle({
      transform: "scale(1.8)",
      transformOrigin: `${x}% ${y}%`,
    });
  };

  const handleMouseLeave = () => {
    setZoomStyle({
      transform: "scale(1)",
      transformOrigin: "center",
    });
  };




  const brandName = product.brand?.name || LABELS.ORGANIZATION.NAME;
  const shippingRateValue = freeShippingThreshold ?? 0;
  const schemaId = `prod-${product.id}`;

  const cleanDescription = product.description?.replace(/<[^>]*>/g, "") || "";
  const orgUrl = (LABELS.ORGANIZATION.URL || "").replace(/\/$/, "");
  const pageUrl = `${orgUrl }/products/${product.slug}`;
  const priceValidUntil = (() => {
    const today = new Date();
    const targetDate = new Date(today.getFullYear(), today.getMonth() + 6, today.getDate());
    const year = targetDate.getFullYear();
    const month = String(targetDate.getMonth() + 1).padStart(2, "0");
    const day = String(targetDate.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  })();
  const offersBase = {
    "priceCurrency": code || LABELS.DEFAULT_CURRENCY || "USD",
    "priceValidUntil": priceValidUntil,
    "itemCondition": "https://schema.org/NewCondition",
    "seller": {
      "@type": "Organization",
      "name": LABELS.ORGANIZATION.NAME,
    },
    "shippingDetails": [
      {
        "@type": "OfferShippingDetails",
        "shippingDestination": {
          "@type": "DefinedRegion",
          "addressCountry": schemaCountry || LABELS.DEFAULT_COUNTRY || "United States"
        },
        "shippingRate": {
          "@type": "MonetaryAmount",
          "value": shippingRateValue,
          "currency": code || LABELS.DEFAULT_CURRENCY || "USD"
        },
        "deliveryTime": {
          "@type": "ShippingDeliveryTime",
          "handlingTime": {
            "@type": "QuantitativeValue",
            "minValue": 0,
            "maxValue": 1,
            "unitCode": "DAY"
          },
          "transitTime": {
            "@type": "QuantitativeValue",
            "minValue": 3,
            "maxValue": 5,
            "unitCode": "DAY"
          }
        }
      }
    ],
    "hasMerchantReturnPolicy": {
      "@type": "MerchantReturnPolicy",
      "applicableCountry": schemaCountry || LABELS.DEFAULT_COUNTRY || "United States",
      "returnPolicyCategory": "https://schema.org/MerchantReturnFiniteReturnWindow",
      "merchantReturnDays": 30,
      "returnMethod": "https://schema.org/ReturnByMail",
      "returnFees": "https://schema.org/FreeReturn"
    }
  };

  const convertedPrice = Number((product.salePrice * exchangeRate).toFixed(2));

  const offers = {
    "@type": "Offer",
    "url": pageUrl,
    "price": convertedPrice,
    "availability": product.stock > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
    ...offersBase
  };

  const productSchema = {
    "@context": "https://schema.org",
    "@type": "Product",
    "@id": schemaId,
    "url": pageUrl,
    "name": product.title,
    "image": product.images.map((img) => img.imageUrl),
    "description": cleanDescription.slice(0, 200),
    "sku": product.sku ? String(product.sku) : product.id.toString(),
    "mpn": product.barcode ? String(product.barcode) : (product.sku ? String(product.sku) : product.id.toString()),
    "brand": {
      "@type": "Brand",
      "name": brandName
    },
    "manufacturer": {
      "@type": "Organization",
      "name": brandName
    },
    // Use default review values for schema consistency
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": 4.5,
      "reviewCount": 5000,
      "bestRating": 5,
      "worstRating": 1
    },
    ...(product.comparePrice && product.comparePrice > 0 ? {
      "offers": offers
    } : {})
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": orgUrl 
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Shop",
        "item": `${orgUrl || ""}/shop`
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": product.title,
        "item": pageUrl
      }
    ]
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(productSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
      <div className={`w-full ${HEADER_V2_DESIGN_SYSTEM.colors.lightGrayBg} py-4 px-4 md:px-6 lg:px-8 font-sans`}>
        <div className="max-w-7xl mx-auto space-y-6">
        
        {/* Breadcrumb strip / Product Title (Mobile) */}
        <div>
          <nav className="hidden md:flex items-center gap-1.5 text-xs text-slate-500 font-medium py-1.5" aria-label="Breadcrumb">
            <Link href="/" className="hover:text-[#D97706] transition-colors">Home</Link>
            <ChevronRight className="w-3.5 h-3.5" />
            <span className="text-slate-800 font-semibold">Products</span>
          </nav>
          <div className="flex md:hidden items-center py-1.5">
            <span className="text-xs font-medium text-slate-500 line-clamp-1">
              {product?.title}
            </span>
          </div>
        </div>

        {/* 1. Product Overview: 3-column Layout (5-4-3 desktop split) */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          
          {/* A. Left Column: Product Gallery (col-span-5 / ~41.7% width) */}
          <div className="lg:col-span-5 flex flex-col space-y-4">

            
            {/* Main Interactive Zoom Display */}
            <div 
              className="relative w-full aspect-square bg-white border border-slate-100 rounded-xl overflow-hidden cursor-zoom-in group shadow-[0_4px_20px_rgba(0,0,0,0.02)]"
              onMouseMove={handleMouseMove}
              onMouseLeave={handleMouseLeave}
              onClick={() => setIsZoomModalOpen(true)}
              suppressHydrationWarning={true}
            >

              <Image
                src={mainImgError ? "/assets/site/images/default.png" : (selectedImage || "/assets/site/images/default.png")}
                alt={product.title}
                fill
                priority
                className="object-contain p-4 transition-transform duration-200 ease-out"
                style={zoomStyle}
                sizes="(max-width: 768px) 100vw, 50vw"
                onError={() => {
                  if (!mainImgError) {
                    setMainImgError(true);
                  }
                }}
              />
              <span className="absolute bottom-3 right-3 bg-slate-900/80 text-white text-[10px] uppercase font-bold tracking-wider px-2.5 py-1.5 rounded-md opacity-0 group-hover:opacity-100 transition-opacity">
                Click to Enlarge
              </span>
            </div>

            {/* Thumbnail Carousel Row */}
            {imageUrls.length > 1 && (
              <div className="flex gap-2.5 overflow-x-auto py-1 scrollbar-none scroll-smooth snap-x snap-mandatory">
                {imageUrls.map((url, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => {
                      setSelectedImage(url);
                      setManualImageSelected(true);
                    }}
                    className={`w-16 h-16 rounded-lg overflow-hidden border-2 bg-white flex-shrink-0 snap-start transition-all ${
                      url === selectedImage 
                        ? "border-[var(--brand-accent)] scale-102 shadow-sm" 
                        : "border-slate-200 hover:border-slate-400"
                    }`}
                  >
                    <NextImage
                      src={url}
                      alt={`Thumbnail ${idx + 1}`}
                      width={64}
                      height={64}
                      className="object-contain p-1 w-full h-full"
                      fallbackSrc="/assets/site/images/default.png"
                    />
                  </button>
                ))}
              </div>
            )}

          </div>

          {/* B. Center Column: Product Information (col-span-4 / ~33.3% width) */}
          <div className="lg:col-span-4 space-y-4">

            
            {/* Brand Title block */}
            <div className="space-y-1">
              {product.brand?.slug ? (
                <Link
                  href={`/vendor/${product.brand.slug}`}
                  className="text-[11px] font-bold tracking-widest text-[var(--brand-accent)] hover:underline hover:text-[var(--brand-accent-hover)] uppercase block w-fit transition-colors"
                >
                  {brandName}
                </Link>
              ) : (
                <span className="text-[11px] font-bold tracking-widest text-[var(--brand-accent)] uppercase block">
                  {brandName}
                </span>
              )}
              <h1 className={`text-2xl font-bold text-slate-900 leading-tight tracking-tight ${HEADER_V2_DESIGN_SYSTEM.typography.fontFamilyHeading}`}>
                {product.title}
              </h1>
            </div>

            {/* Rating block */}
            {Boolean(product.totalRating && product.totalRating > 0) && (
              <div className="flex items-center gap-2 pb-1 border-b border-slate-100">
                <RatingStars rating={product.averageRating} reviewCount={product.totalRating} />
                <span className="text-xs text-slate-500 font-medium">({product.totalRating} buyer reviews)</span>
              </div>
            )}

            {/* Pricing Section */}
            <div className="space-y-1">
              <div className="flex items-baseline gap-2 flex-wrap">
                <Price
                  amount={displaySalePrice}
                  className="text-2xl font-black text-slate-900"
                />
                {hasDiscount && (
                  <>
                    <Price
                      amount={displayOriginalPrice}
                      className="text-slate-400 text-xs line-through font-semibold"
                    />
                    <span className="bg-emerald-50 text-emerald-700 text-xs font-bold px-2 py-0.5 rounded-md border border-emerald-100 flex items-center gap-1">
                      Save <SavePercentage originalPrice={displayOriginalPrice} discountedPrice={displaySalePrice} />
                    </span>
                  </>
                )}
              </div>
              <p className="text-[10px] text-slate-400 font-medium leading-none">Price includes tariff and duties.</p>
            </div>

            {/* Variant Selectors */}
            {attributeNames.map((attrName) => (
              <div key={attrName} className="space-y-2 pt-2 border-t border-slate-100">
                <span className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
                  Select {attrName}:
                  <span className="text-slate-500 font-normal">
                    {selectedAttributes[attrName]}
                  </span>
                </span>
                <div className="flex flex-wrap gap-2">
                  {getAttributeValues(attrName).map((value) => {
                    const isDisabled = !isValueValid(attrName, value);
                    const isActive = selectedAttributes[attrName] === value;

                    return (
                      <button
                        key={value}
                        type="button"
                        disabled={isDisabled}
                        onClick={() => handleAttributeSelect(attrName, value)}
                        className={`px-3 py-1.5 rounded-md border text-xs font-semibold transition-all ${
                          isActive 
                            ? "border-[var(--brand-accent)] bg-[var(--brand-accent)]/5 text-[var(--brand-accent)]"
                            : isDisabled
                            ? "border-slate-200 text-slate-300 cursor-not-allowed line-through"
                            : "border-slate-300 text-slate-700 bg-white hover:bg-slate-50 hover:border-slate-400"
                        }`}
                      >
                        {value}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}

            {/* Free Shipping Callout - Placed below variant selection */}
            {freeShippingThreshold !== null && (
              <div className="mt-3 flex items-center gap-2 bg-emerald-50 text-emerald-700 border border-emerald-100 px-3 py-1.5 rounded-lg text-xs font-semibold w-fit">
                <Truck className="w-4 h-4 text-emerald-600 animate-bounce" />
                <span>
                  Free shipping on orders over <span className="font-bold inline text-xs">{symbol}{freeShippingThreshold.toFixed(2)} {code.toUpperCase()}</span>
                </span>
              </div>
            )}

            {/* Promo Offers Callout */}
            <div className="space-y-2.5">
              {/* Offer 1: 5% OFF above 10 */}
              <div className="flex items-center gap-3 bg-gradient-to-r from-rose-50/80 to-pink-50/80 border border-rose-200/80 rounded-xl px-4 py-3 shadow-xs">
                <div className="w-8 h-8 rounded-full bg-rose-100 flex items-center justify-center shrink-0">
                  <span className="text-xs font-black text-rose-700">5%</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold text-rose-900 leading-tight flex items-center gap-1 flex-wrap">
                    <span>🎉 5% OFF on orders over</span>
                    <Price amount={10} className="font-extrabold inline text-xs text-rose-900" />
                  </p>
                  <p className="text-[11px] text-rose-700 mt-0.5 flex items-center gap-1">
                    <span>Use code</span>
                    <span className="font-black bg-rose-200 text-rose-900 px-1.5 py-0.5 rounded tracking-wider text-[10px]">
                      FLAT5
                    </span>
                    <span>at checkout</span>
                  </p>
                </div>
              </div>
            </div>

            {/* Payment & Logistics Badges */}

            <div className="flex flex-wrap items-center gap-6 pt-4 pb-4 border-t border-b border-slate-100 my-2 bg-slate-50 rounded-xl px-4">
              {/* Payment Methods */}
              <div className="flex flex-col gap-1.5">
                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Guaranteed Safe Checkout</span>
                <div className="flex items-center gap-3 mt-1 opacity-90">
                  <Image src={getAssetPath("/assets/site/images/stripe.png")} alt="Stripe" width={52} height={20} className="h-5 w-auto object-contain" />
                  <Image src={getAssetPath("/assets/site/images/paypal.png")} alt="PayPal" width={52} height={20} className="h-5 w-auto object-contain" />
                  <Image src={getAssetPath("/assets/site/images/razorpay.png")} alt="Razorpay" width={64} height={18} className="h-5 w-auto object-contain" />
                </div>
              </div>

              {/* Vertical divider */}
              <div className="hidden sm:block w-px h-10 bg-slate-200 self-center" />

              {/* Logistics Partners */}
              <div className="flex flex-col gap-1.5">
                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Fast Worldwide Shipping</span>
                <div className="flex items-center gap-3 mt-1 opacity-90">
                  <Image src={getAssetPath("/assets/site/images/fedexnew.png")} alt="FedEx" width={60} height={20} className="h-6 w-auto object-contain" />
                  <Image src={getAssetPath("/assets/site/images/dhlnew.png")} alt="DHL" width={60} height={20} className="h-6 w-auto object-contain" />
                </div>
              </div>
            </div>


          </div>

          {/* C. Right Column: Compact Sticky Purchase Card (col-span-3 / 25% width) */}
          <div className="lg:col-span-3 lg:sticky lg:top-6 self-start">
            <div className={`bg-white border border-slate-100 rounded-xl p-5 ${HEADER_V2_DESIGN_SYSTEM.elevation.card} hover:shadow-[0_10px_20px_rgba(0,0,0,0.05)] transition-all duration-300 space-y-4`}>
              
              {/* Price + Quantity inline on mobile, stacked on desktop */}
              <div className="flex lg:flex-col items-center justify-center gap-6 lg:gap-0">

                <div className="space-y-1 text-center flex flex-col items-center justify-center">
                  <span className="text-[11px] text-slate-400 font-bold uppercase tracking-wider block">Price</span>
                  <div className="flex items-baseline justify-center gap-1.5">
                    <Price
                      amount={displaySalePrice}
                      className="text-2xl lg:text-2xl font-black text-[var(--brand-accent)]"
                    />
                  </div>
                </div>

                {/* Quantity Selector if not in cart */}
                {!isInCart ? (
                  <div className="space-y-1.5 flex flex-col items-center justify-center text-center">
                    <span className="text-xs font-bold text-slate-800">Quantity</span>
                    <div className="flex justify-center">
                      <QuantitySelector 
                        value={quantity} 
                        onChange={handleQuantityChange} 
                        max={stockCount}
                      />
                    </div>
                  </div>
                ) : (
                  <div className="bg-emerald-50 text-emerald-800 text-xs font-semibold px-3 py-2 rounded-lg border border-emerald-100 text-center flex items-center justify-center gap-1.5">
                    <CheckCircle className="w-4 h-4 text-emerald-600 shrink-0" />
                    <span>Item already in cart</span>
                  </div>
                )}

              </div>


              {/* Action Buttons */}
              <div className="space-y-2 pt-1">
                {isInCart ? (
                  <button
                    type="button"
                    onClick={() => fastPush("/cart")}
                    className={`w-full py-2.5 ${HEADER_V2_DESIGN_SYSTEM.colors.accentBg} ${HEADER_V2_DESIGN_SYSTEM.colors.accentBgHover} text-white font-bold text-xs uppercase tracking-wider rounded-lg shadow-sm transition-colors cursor-pointer flex items-center justify-center gap-1.5`}
                  >
                    <ShoppingCart className="w-4 h-4" />
                    View Shopping Cart
                  </button>
                ) : (
                  <>
                    <button
                      type="button"
                      disabled={stockCount <= 0}
                      onClick={handleAddToCart}
                      className={`w-full py-2.5 ${HEADER_V2_DESIGN_SYSTEM.colors.primaryBg} hover:bg-[var(--brand-accent-hover)] text-white font-bold text-xs uppercase tracking-wider rounded-lg shadow-sm transition-colors cursor-pointer disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed`}
                    >
                      Add To Cart
                    </button>
                    <button
                      type="button"
                      disabled={stockCount <= 0}
                      onClick={handleBuyNow}
                      className={`w-full py-2.5 ${HEADER_V2_DESIGN_SYSTEM.colors.accentBg} ${HEADER_V2_DESIGN_SYSTEM.colors.accentBgHover} text-white font-bold text-xs uppercase tracking-wider rounded-lg shadow-sm transition-colors cursor-pointer disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed`}
                    >
                      Buy It Now
                    </button>
                  </>
                )}
              </div>

              {/* Wishlist toggle */}
              <div className="flex items-center justify-center pt-2 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => {
                    if (!isLoggedIn) {
                      toast.info("Please sign in to manage your wishlist");
                      return;
                    }
                    toggleWishlist(product);
                  }}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-600 hover:text-[var(--brand-accent)] transition-colors"
                >
                  <Heart className={`w-4 h-4 ${isInWishlist(product.id) ? "text-rose-500 fill-current" : ""}`} />
                  {isInWishlist(product.id) ? "Saved in Wishlist" : "Save to Wishlist"}
                </button>
              </div>

              {/* Security indicator */}
              <div className="bg-slate-50 rounded-lg p-3 flex flex-col items-center justify-center gap-2 border border-slate-100">
                <div className="flex items-center gap-1.5 text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                  <Lock className="w-3.5 h-3.5 text-slate-400" />
                  <span>100% Secure Transaction</span>
                </div>
                <div className="flex items-center gap-2.5 mt-1 justify-center opacity-85">
                  <Image src={getAssetPath("/assets/site/images/stripe.png")} alt="Stripe" width={40} height={16} className="h-3.5 w-auto object-contain" />
                  <Image src={getAssetPath("/assets/site/images/paypal.png")} alt="PayPal" width={40} height={16} className="h-3.5 w-auto object-contain" />
                  <Image src={getAssetPath("/assets/site/images/razorpay.png")} alt="Razorpay" width={50} height={14} className="h-4 w-auto object-contain" />
                </div>
              </div>

            </div>
          </div>

        </div>

        {/* 2. Trust Section (Horizontal Row below Overview) */}
        <div className="pt-4 border-t border-slate-200">

          {/* Mobile: Swiper slider */}
          <div className="block md:hidden">
            <Swiper
              modules={[Autoplay]}
              slidesPerView={2.2}
              spaceBetween={12}
              loop
              autoplay={{ delay: 2500, disableOnInteraction: false }}
            >
              <SwiperSlide>
                <div className={`bg-white border border-slate-100 rounded-xl p-4 flex flex-col items-center text-center space-y-2 ${HEADER_V2_DESIGN_SYSTEM.elevation.card}`}>
                  <ShieldCheck className="w-6 h-6 text-[var(--brand-accent)]" />
                  <div className="leading-tight">
                    <span className="block text-xs font-bold text-slate-800">Secure Payments</span>
                    <span className="text-[10px] text-slate-400 font-medium">SSL Encrypted Checkout</span>
                  </div>
                </div>
              </SwiperSlide>
              <SwiperSlide>
                <div className={`bg-white border border-slate-100 rounded-xl p-4 flex flex-col items-center text-center space-y-2 ${HEADER_V2_DESIGN_SYSTEM.elevation.card}`}>
                  <Truck className="w-6 h-6 text-[var(--brand-accent)]" />
                  <div className="leading-tight">
                    <span className="block text-xs font-bold text-slate-800">Worldwide Shipping</span>
                    <span className="text-[10px] text-slate-400 font-medium">DHL &amp; FedEx Partners</span>
                  </div>
                </div>
              </SwiperSlide>
              <SwiperSlide>
                <div className={`bg-white border border-slate-100 rounded-xl p-4 flex flex-col items-center text-center space-y-2 ${HEADER_V2_DESIGN_SYSTEM.elevation.card}`}>
                  <RotateCcw className="w-6 h-6 text-[var(--brand-accent)]" />
                  <div className="leading-tight">
                    <span className="block text-xs font-bold text-slate-800">Easy Returns</span>
                    <span className="text-[10px] text-slate-400 font-medium">Hassle-free 30-Day policy</span>
                  </div>
                </div>
              </SwiperSlide>
              <SwiperSlide>
                <div className={`bg-white border border-slate-100 rounded-xl p-4 flex flex-col items-center text-center space-y-2 ${HEADER_V2_DESIGN_SYSTEM.elevation.card}`}>
                  <CheckCircle className="w-6 h-6 text-[var(--brand-accent)]" />
                  <div className="leading-tight">
                    <span className="block text-xs font-bold text-slate-800">Quality Inspected</span>
                    <span className="text-[10px] text-slate-400 font-medium">Triple-stage check verified</span>
                  </div>
                </div>
              </SwiperSlide>
            </Swiper>
          </div>

          {/* Desktop: 4-column grid */}
          <div className="hidden md:grid grid-cols-4 gap-4">
            <div className={`bg-white border border-slate-100 rounded-xl p-4 flex flex-col items-center text-center space-y-2 ${HEADER_V2_DESIGN_SYSTEM.elevation.card} hover:shadow-[0_10px_20px_rgba(0,0,0,0.05)] transition-all duration-300`}>
              <ShieldCheck className="w-6 h-6 text-[var(--brand-accent)]" />
              <div className="leading-tight">
                <span className="block text-xs font-bold text-slate-800">Secure Payments</span>
                <span className="text-[10px] text-slate-400 font-medium">SSL Encrypted Checkout</span>
              </div>
            </div>
            <div className={`bg-white border border-slate-100 rounded-xl p-4 flex flex-col items-center text-center space-y-2 ${HEADER_V2_DESIGN_SYSTEM.elevation.card} hover:shadow-[0_10px_20px_rgba(0,0,0,0.05)] transition-all duration-300`}>
              <Truck className="w-6 h-6 text-[var(--brand-accent)]" />
              <div className="leading-tight">
                <span className="block text-xs font-bold text-slate-800">Worldwide Shipping</span>
                <span className="text-[10px] text-slate-400 font-medium">DHL &amp; FedEx Partners</span>
              </div>
            </div>
            <div className={`bg-white border border-slate-100 rounded-xl p-4 flex flex-col items-center text-center space-y-2 ${HEADER_V2_DESIGN_SYSTEM.elevation.card} hover:shadow-[0_10px_20px_rgba(0,0,0,0.05)] transition-all duration-300`}>
              <RotateCcw className="w-6 h-6 text-[var(--brand-accent)]" />
              <div className="leading-tight">
                <span className="block text-xs font-bold text-slate-800">Easy Returns</span>
                <span className="text-[10px] text-slate-400 font-medium">Hassle-free 30-Day policy</span>
              </div>
            </div>
            <div className={`bg-white border border-slate-100 rounded-xl p-4 flex flex-col items-center text-center space-y-2 ${HEADER_V2_DESIGN_SYSTEM.elevation.card} hover:shadow-[0_10px_20px_rgba(0,0,0,0.05)] transition-all duration-300`}>
              <CheckCircle className="w-6 h-6 text-[var(--brand-accent)]" />
              <div className="leading-tight">
                <span className="block text-xs font-bold text-slate-800">Quality Inspected</span>
                <span className="text-[10px] text-slate-400 font-medium">Triple-stage check verified</span>
              </div>
            </div>
          </div>

        </div>


        {/* Tabbed Content Area (Description, Reviews) */}
        <div id="product-description-section" className="bg-white border border-slate-100 rounded-xl overflow-hidden shadow-sm">
          
          {/* Tab buttons bar */}
          <div className="flex border-b border-slate-100 bg-slate-50/50">
            <button
              type="button"
              onClick={() => setActiveTab("description")}
              className={`px-3.5 sm:px-5 py-3.5 text-xs font-bold uppercase tracking-wider border-b-2 transition-all flex items-center gap-1.5 whitespace-nowrap ${
                activeTab === "description" 
                  ? "border-[var(--brand-accent)] text-[var(--brand-accent)] bg-white" 
                  : "border-transparent text-slate-500 hover:text-slate-800"
              }`}
            >
              <FileText className="w-3.5 h-3.5" />
              Description
            </button>
            {Boolean(product?.faqs && product.faqs.length > 0) && (
              <button
                type="button"
                onClick={() => setActiveTab("faqs")}
                className={`px-3.5 sm:px-5 py-3.5 text-xs font-bold uppercase tracking-wider border-b-2 transition-all flex items-center gap-1.5 whitespace-nowrap ${
                  activeTab === "faqs" 
                    ? "border-[var(--brand-accent)] text-[var(--brand-accent)] bg-white" 
                    : "border-transparent text-slate-500 hover:text-slate-800"
                }`}
              >
                <HelpCircle className="w-3.5 h-3.5" />
                FAQs ({product?.faqs?.length})
              </button>
            )}
          </div>

          {/* Tab content area */}
          <div className="p-6 md:p-8">
            
            {/* Tab: Description */}
            {activeTab === "description" && (
              <div 
                className="text-xs text-slate-600 leading-relaxed space-y-3"
                dangerouslySetInnerHTML={{ __html: product?.description || "" }}
              />
            )}

            {/* Tab: FAQs */}
            {activeTab === "faqs" && Boolean(product?.faqs && product.faqs.length > 0) && (
              <div className="space-y-5">
                {/* Header Banner */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 sm:p-5 rounded-2xl bg-[var(--brand-surface-light)] border border-[var(--brand-accent)]/20 shadow-2xs">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl bg-[var(--brand-accent)] text-white flex items-center justify-center shadow-xs shrink-0">
                      <HelpCircle className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="text-sm font-extrabold text-slate-900 leading-tight">
                        Frequently Asked Questions
                      </h3>
                      <p className="text-xs text-slate-500 mt-0.5 font-medium">
                        Got questions? We&apos;ve got answers for everything about this product.
                      </p>
                    </div>
                  </div>
                  <span className="text-[11px] font-extrabold text-[var(--brand-accent)] bg-[var(--brand-accent)]/10 border border-[var(--brand-accent)]/20 px-3 py-1 rounded-full w-fit shrink-0">
                    {(product.faqs || []).length} {(product.faqs || []).length === 1 ? "Question" : "Questions"}
                  </span>
                </div>

                {/* Accordion List */}
                <div className="space-y-3">
                  {product?.faqs?.map((faq: { question: string; answer: string }, idx: number) => {
                    const isOpen = openFaqIndexes.includes(idx);
                    return (
                      <div
                        key={idx}
                        className={`rounded-2xl border transition-all duration-200 overflow-hidden ${
                          isOpen
                            ? "bg-white border-[var(--brand-accent)] shadow-md ring-2 ring-[var(--brand-accent)]/20"
                            : "bg-slate-50/70 hover:bg-white border-slate-200/80 hover:border-slate-300 shadow-2xs"
                        }`}
                      >
                        <button
                          type="button"
                          onClick={() => toggleFaq(idx)}
                          className="w-full px-5 py-4 flex items-center justify-between gap-4 text-left cursor-pointer group"
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <span
                              className={`w-7 h-7 rounded-lg text-xs font-black flex items-center justify-center shrink-0 transition-colors ${
                                isOpen
                                  ? "bg-[var(--brand-accent)] text-white shadow-2xs"
                                  : "bg-slate-200 text-slate-700 group-hover:bg-[var(--brand-accent)]/10 group-hover:text-[var(--brand-accent)]"
                              }`}
                            >
                              Q{idx + 1}
                            </span>
                            <span className="text-xs sm:text-sm font-bold text-slate-900 leading-snug">
                              {faq.question}
                            </span>
                          </div>
                          <div
                            className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 transition-transform duration-200 ${
                              isOpen
                                ? "bg-[var(--brand-accent)]/15 text-[var(--brand-accent)] rotate-180"
                                : "bg-slate-100 text-slate-500 group-hover:bg-slate-200"
                            }`}
                          >
                            <ChevronDown className="w-4 h-4" />
                          </div>
                        </button>

                        {isOpen && (
                          <div className="px-5 pb-4 pt-0">
                            <div className="pl-4 sm:pl-10 pr-4 py-3.5 bg-[var(--brand-surface-light)] rounded-xl border border-[var(--brand-accent)]/20 flex items-start gap-2.5">
                              <div className="w-5 h-5 rounded-full bg-[var(--brand-primary)] text-white text-[10px] font-black flex items-center justify-center shrink-0 mt-0.5">
                                A
                              </div>
                              <p className="text-xs text-slate-700 leading-relaxed font-medium">
                                {faq.answer}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Tab: Reviews */}
            {activeTab === "reviews" && (
              <div className="space-y-6">
                <div className="flex flex-col md:flex-row gap-6 items-center p-5 bg-slate-50 rounded-xl border border-slate-100">
                  <div className="text-center md:border-r md:border-slate-200 md:pr-8">
                    <span className="text-4xl font-black text-slate-900 block leading-none mb-1">
                      {product?.averageRating || "4.8"}
                    </span>
                    <RatingStars rating={product?.averageRating || 4.8} reviewCount={product?.totalRating || 0} />
                    <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider block mt-1.5">
                      Average Rating
                    </span>
                  </div>
                  <div className="space-y-1 text-xs text-slate-600 flex-1">
                    <p className="font-bold text-slate-800">Verified Buyer Reviews</p>
                    <p>Every review is verified and collected directly from purchase receipts. Showing latest reviews for this cookware.</p>
                  </div>
                </div>

                <div className="space-y-4 divide-y divide-slate-100">
                  <div className="pt-4 first:pt-0 space-y-1.5">
                    <div className="flex items-center gap-1.5">
                      <RatingStars rating={5} />
                      <span className="text-xs font-bold text-slate-800">Karan S.</span>
                      <span className="text-[10px] text-emerald-600 font-bold border border-emerald-100 bg-emerald-50 px-1.5 py-0.5 rounded">Verified Purchase</span>
                    </div>
                    <p className="text-xs font-semibold text-slate-800">Heirloom Grade Quality</p>
                    <p className="text-xs text-slate-600 leading-relaxed">Absolutely stunning finish! Heavy brass weight and distributes heat beautifully. Reminds me of traditional cookware at home.</p>
                  </div>
                  <div className="pt-4 space-y-1.5">
                    <div className="flex items-center gap-1.5">
                      <RatingStars rating={5} />
                      <span className="text-xs font-bold text-slate-800">Ananya R.</span>
                      <span className="text-[10px] text-emerald-600 font-bold border border-emerald-100 bg-emerald-50 px-1.5 py-0.5 rounded">Verified Purchase</span>
                    </div>
                    <p className="text-xs font-semibold text-slate-800">Excellent Craftsmanship</p>
                    <p className="text-xs text-slate-600 leading-relaxed">Superb packaging, fast delivery. Cooking food in this brassware tastes incredibly different. 100% recommended.</p>
                  </div>
                </div>
              </div>
            )}

          </div>
        </div>

     
      </div>

      {/* Recommended Products sections */}
      <div className="max-w-7xl mx-auto mt-12">
        <PeopleAlsoViewed products={relatedProducts} />
        <InterestedProducts products={alsoBought} />
      </div>

      {/* 6. Sticky Bottom Cart actions for mobile viewports, and Floating Action Pill for desktop viewports */}
      {/* Mobile Sticky Bar */}
      <div
        className="fixed left-0 right-0 z-50 bg-white border-t border-slate-200 px-4 py-3 flex items-center justify-between gap-4 md:hidden shadow-[0_-4px_12px_rgba(0,0,0,0.06)] bottom-0"
      >
        <div className="flex flex-col items-center justify-center text-center leading-none">
          <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Subtotal</span>
          <Price amount={displaySalePrice * quantity} className="text-xl font-black text-[var(--brand-accent)] mt-0.5" />
        </div>
        <div className="flex gap-2 flex-1 max-w-[240px]">
          {isInCart ? (
            <button
              type="button"
              onClick={() => fastPush("/cart")}
              className={`w-full py-2.5 ${HEADER_V2_DESIGN_SYSTEM.colors.primaryBg} text-white font-bold text-xs uppercase tracking-wider rounded-lg shadow-sm text-center`}
            >
              View Cart
            </button>
          ) : (
            <>
              <button
                type="button"
                disabled={stockCount <= 0}
                onClick={handleAddToCart}
                className={`flex-1 py-2.5 ${HEADER_V2_DESIGN_SYSTEM.colors.primaryBg} text-white font-bold text-xs uppercase tracking-wider rounded-lg disabled:bg-slate-200 disabled:text-slate-400`}
              >
                Add
              </button>
              <button
                type="button"
                disabled={stockCount <= 0}
                onClick={handleBuyNow}
                className={`flex-1 py-2.5 ${HEADER_V2_DESIGN_SYSTEM.colors.accentBg} text-white font-bold text-xs uppercase tracking-wider rounded-lg disabled:bg-slate-200 disabled:text-slate-400`}
              >
                Buy
              </button>
            </>
          )}
        </div>
      </div>

      {/* Desktop Floating Action Pill */}
      <div
        className="fixed left-1/2 -translate-x-1/2 z-50 bg-white/90 backdrop-blur-md border border-slate-100 rounded-full py-2.5 px-6 items-center gap-6 hidden lg:flex shadow-[0_20px_50px_rgba(0,0,0,0.12)] bottom-6 opacity-100 pointer-events-auto"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-slate-50 border border-slate-100 rounded-lg overflow-hidden flex items-center justify-center relative">
            <Image
              src={selectedImage || "/assets/site/images/default.png"}
              alt="Floating Product Thumb"
              fill
              className="object-cover"
              sizes="40px"
            />
          </div>
          <div className="flex flex-col justify-center">
            <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider leading-none">Subtotal</span>
            <Price amount={displaySalePrice * quantity} className="text-lg font-black text-[var(--brand-accent)] mt-0.5 leading-none" />
          </div>
        </div>

        <div className="h-8 w-px bg-slate-100" />

        <p className="text-xs font-semibold text-slate-800 max-w-[180px] truncate leading-none">
          {product?.title}
        </p>

        <div className="h-8 w-px bg-slate-100" />

        <div className="flex items-center gap-2">
          {isInCart ? (
            <button
              type="button"
              onClick={() => fastPush("/cart")}
              className={`px-4 py-2 ${HEADER_V2_DESIGN_SYSTEM.colors.accentBg} ${HEADER_V2_DESIGN_SYSTEM.colors.accentBgHover} text-white font-bold text-xs uppercase tracking-wider rounded-full shadow-sm transition-colors cursor-pointer`}
            >
              View Cart
            </button>
          ) : (
            <>
              <button
                type="button"
                disabled={stockCount <= 0}
                onClick={handleAddToCart}
                className={`px-4 py-2 ${HEADER_V2_DESIGN_SYSTEM.colors.primaryBg} hover:bg-[var(--brand-accent-hover)] text-white font-bold text-xs uppercase tracking-wider rounded-full shadow-sm transition-colors cursor-pointer disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed`}
              >
                Add to Cart
              </button>
              <button
                type="button"
                disabled={stockCount <= 0}
                onClick={handleBuyNow}
                className={`px-4 py-2 ${HEADER_V2_DESIGN_SYSTEM.colors.accentBg} ${HEADER_V2_DESIGN_SYSTEM.colors.accentBgHover} text-white font-bold text-xs uppercase tracking-wider rounded-full shadow-sm transition-colors cursor-pointer disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed`}
              >
                Buy Now
              </button>
            </>
          )}
        </div>
      </div>

      {/* 7. Image Click Enlarge Modal */}
      {isZoomModalOpen && (
        <div className="fixed inset-0 z-[2000] bg-black/90 flex items-center justify-center p-4">
          <button
            type="button"
            onClick={() => setIsZoomModalOpen(false)}
            className="absolute top-4 right-4 bg-white/10 hover:bg-white/20 text-white rounded-full p-2.5 cursor-pointer transition-colors"
          >
            ✕
          </button>
          <div className="relative max-w-4xl max-h-[85vh] w-full h-full">
            <Image
              src={mainImgError ? "/assets/site/images/default.png" : (selectedImage || "/assets/site/images/default.png")}
              alt="Expanded Preview"
              fill
              className="object-contain"
              sizes="90vw"
            />
          </div>
        </div>
      )}

    </div>
    </>
  );
}
